import type { ArtworkLayer, Fill, TextStyle, WearableConfig } from "../types";
import { getShirtAxes, shirtRotationRad } from "../utils/shirtAxes";
import { resolveFillStyle } from "../utils/fill";

/**
 * In-browser texture composition pipeline.
 *
 * We maintain TWO offscreen canvases per editor session:
 *   - baseCanvas: the albedo / base color map (applied as material.map).
 *   - emissiveCanvas: the emissive map (applied as material.emissiveMap).
 *
 * Both are re-drawn when editor state changes, and used as the source of
 * three.js CanvasTextures, the downloadable PNG, and GLB export.
 *
 * Per-layer behavior depends on `layer.type`:
 *   - mix       -> drawn on baseCanvas with source-over
 *   - multiply  -> drawn on baseCanvas with multiply composite
 *   - emission  -> drawn on emissiveCanvas (only), brightness baked into
 *                  alpha = opacity * intensity / 2 so the material can
 *                  keep a fixed emissiveIntensity = 2 and cover 0..2.
 */

export interface ComposeInput {
  config: WearableConfig;
  /** The paint applied behind every layer (solid or gradient). */
  baseFill: Fill;
  layers: ArtworkLayer[];
  images: Map<string, HTMLImageElement>;
  /**
   * When true, paint the frontBox and backBox onto the texture along
   * with crosshairs at their centres and arrows for the shirt-aligned
   * right/up axes. Useful for visually verifying on the 3D model that
   * the detected printable regions line up with the chest/back.
   */
  debugOverlay?: boolean;
}

export function createTextureCanvas(size: number): HTMLCanvasElement {
  const c = document.createElement("canvas");
  c.width = size;
  c.height = size;
  return c;
}

/**
 * Compose both the base color and the emissive textures for the current
 * editor state in a single pass. Either canvas may be reused from the
 * previous compose; they get resized in-place if needed.
 */
export function composeTextures(
  baseCanvas: HTMLCanvasElement,
  emissiveCanvas: HTMLCanvasElement,
  input: ComposeInput,
): void {
  const { config, baseFill, layers, images, debugOverlay } = input;
  const size = config.textureSize;

  for (const canvas of [baseCanvas, emissiveCanvas]) {
    if (canvas.width !== size || canvas.height !== size) {
      canvas.width = size;
      canvas.height = size;
    }
  }

  const baseCtx = baseCanvas.getContext("2d");
  const emiCtx = emissiveCanvas.getContext("2d");
  if (!baseCtx || !emiCtx) return;

  // --- Base layer fill (albedo starts with the chosen base fill, which
  //     can be solid or a linear gradient spanning the whole texture). ---
  baseCtx.save();
  baseCtx.globalCompositeOperation = "source-over";
  baseCtx.globalAlpha = 1;
  baseCtx.fillStyle = resolveFillStyle(baseCtx, baseFill, size / 2, size / 2, size, size);
  baseCtx.fillRect(0, 0, size, size);
  baseCtx.restore();

  // --- Emissive layer starts fully black (no emission). ---
  emiCtx.save();
  emiCtx.globalCompositeOperation = "source-over";
  emiCtx.globalAlpha = 1;
  emiCtx.fillStyle = "#000000";
  emiCtx.fillRect(0, 0, size, size);
  emiCtx.restore();

  // Draw layers in order. First layer in the array is drawn first (bottom),
  // last is drawn last (top). Each layer routes to the appropriate canvas
  // based on its `type`.
  for (const layer of layers) {
    if (!layer.visible) continue;

    const box = layer.side === "front" ? config.frontBox : config.backBox;
    const axes = getShirtAxes(box);

    // Resolve the renderer once per layer, then route it to the right
    // canvas/compositing mode based on layer.type.
    const draw = (
      ctx: CanvasRenderingContext2D,
      composite: GlobalCompositeOperation,
      alpha: number,
    ): void => {
      if (layer.kind === "text") {
        if (!layer.text) return;
        drawTextLayer(ctx, layer, layer.text, axes, composite, alpha);
      } else {
        const img = images.get(layer.id);
        if (!img || !img.complete || img.naturalWidth === 0) return;
        drawImageLayer(ctx, layer, img, axes, composite, alpha);
      }
    };

    if (layer.type === "emission") {
      // Brightness = opacity * intensity/2. When the material's
      // emissiveIntensity is fixed at 2, a canvas pixel at alpha=1 maps
      // to an emission value of 2, alpha=0.5 maps to 1.0, etc.
      const intensity = clamp(layer.emissiveIntensity, 0, 2);
      const alpha = clamp01(layer.opacity) * (intensity / 2);
      if (alpha <= 0) continue;
      draw(emiCtx, "source-over", alpha);
    } else {
      const op: GlobalCompositeOperation =
        layer.type === "multiply" ? "multiply" : "source-over";
      draw(baseCtx, op, clamp01(layer.opacity));
    }
  }

  if (debugOverlay) {
    drawDebugOverlay(baseCtx, config);
  }
}

function drawImageLayer(
  ctx: CanvasRenderingContext2D,
  layer: ArtworkLayer,
  img: HTMLImageElement,
  axes: ReturnType<typeof getShirtAxes>,
  composite: GlobalCompositeOperation,
  alpha: number,
): void {
  ctx.save();

  ctx.globalCompositeOperation = composite;
  ctx.globalAlpha = alpha;
  ctx.translate(layer.x, layer.y);
  // Combine the user-controlled rotation with the UV island's
  // rotation so artwork appears upright on the 3D shirt.
  ctx.rotate((layer.rotation * Math.PI) / 180 + shirtRotationRad(axes));

  // Negative scale mirrors the artwork horizontally on the shirt.
  // The sign flip is applied AFTER the shirt-axis rotation so "left"
  // and "right" correspond to the shirt's left/right, not to the
  // underlying UV pixel axes.
  if (layer.scale < 0) ctx.scale(-1, 1);
  const absScale = Math.abs(layer.scale);
  const w = layer.naturalWidth * absScale;
  const h = layer.naturalHeight * absScale;
  ctx.drawImage(img, -w / 2, -h / 2, w, h);

  ctx.restore();
}

/** Fallback line-height multiplier when a text layer has no explicit value. */
const DEFAULT_LINE_HEIGHT = 1.2;

function lineHeightOf(style: TextStyle): number {
  const v = style.lineHeight;
  if (typeof v !== "number" || !Number.isFinite(v) || v <= 0) {
    return DEFAULT_LINE_HEIGHT;
  }
  return v;
}

function letterSpacingOf(style: TextStyle): number {
  const v = style.letterSpacing;
  return typeof v === "number" && Number.isFinite(v) ? v : 0;
}

function letterTiltOf(style: TextStyle): number {
  const v = style.letterTilt;
  return typeof v === "number" && Number.isFinite(v) ? v : 0;
}

function letterTiltModeOf(style: TextStyle): "uniform" | "alternate" {
  return style.letterTiltMode === "alternate" ? "alternate" : "uniform";
}

function drawTextLayer(
  ctx: CanvasRenderingContext2D,
  layer: ArtworkLayer,
  style: TextStyle,
  axes: ReturnType<typeof getShirtAxes>,
  composite: GlobalCompositeOperation,
  alpha: number,
): void {
  if (!style.text) return;
  ctx.save();

  ctx.globalCompositeOperation = composite;
  ctx.globalAlpha = alpha;
  ctx.translate(layer.x, layer.y);
  ctx.rotate((layer.rotation * Math.PI) / 180 + shirtRotationRad(axes));
  if (layer.scale < 0) ctx.scale(-1, 1);
  const absScale = Math.abs(layer.scale);

  const size = style.fontSize * absScale;
  ctx.font = cssFont(style, size);
  // Glyphs are positioned manually so we can support letter spacing
  // and per-letter tilt. We centre each glyph on its translate point
  // and let ctx's alignment handle the rest.
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";

  const lines = splitLines(style.text);
  const lineHeightPx = size * lineHeightOf(style);
  const spacingPx = letterSpacingOf(style) * absScale;
  const tiltDeg = letterTiltOf(style);
  const tiltMode = letterTiltModeOf(style);
  const yStart = -((lines.length - 1) / 2) * lineHeightPx;

  // Resolve fillStyle AFTER all layer-level transforms are applied so
  // that when `style.color` is a gradient its coordinate space matches
  // the text block. We create it once and reuse it across all lines /
  // glyphs so the whole text shares one continuous gradient.
  const blockWidth = measureBlockWidth(ctx, lines, spacingPx) || size;
  const blockHeight = Math.max(size, lineHeightPx * lines.length);
  ctx.fillStyle = resolveFillStyle(
    ctx,
    toFill(style.color),
    0,
    0,
    blockWidth,
    blockHeight,
  );

  for (let i = 0; i < lines.length; i++) {
    const y = yStart + i * lineHeightPx;
    drawTextLine(ctx, lines[i], y, spacingPx, tiltDeg, tiltMode);
  }

  ctx.restore();
}

/**
 * Normalise a legacy `string` color into a `Fill`, so old/partial
 * TextStyle values still render during migration.
 */
function toFill(color: Fill | string | undefined): Fill {
  if (!color) return { kind: "solid", color: "#ffffff" };
  if (typeof color === "string") return { kind: "solid", color };
  return color;
}

/** Widest line's total advance width (glyphs + letter spacing). */
function measureBlockWidth(
  ctx: CanvasRenderingContext2D,
  lines: string[],
  spacingPx: number,
): number {
  let max = 0;
  for (const line of lines) {
    const chars = Array.from(line || " ");
    let w = 0;
    for (const c of chars) w += ctx.measureText(c).width;
    w += Math.max(0, chars.length - 1) * spacingPx;
    if (w > max) max = w;
  }
  return max;
}

/**
 * Render a single line of text centred on (0, y), laying out each
 * character individually so we can honour letter spacing and the
 * alternating per-letter tilt.
 */
function drawTextLine(
  ctx: CanvasRenderingContext2D,
  line: string,
  y: number,
  spacingPx: number,
  tiltDeg: number,
  tiltMode: "uniform" | "alternate",
): void {
  if (!line) return;

  // `Array.from` splits the string into code-point units so emoji and
  // surrogate-pair characters aren't torn in half.
  const chars = Array.from(line);
  const widths = chars.map((c) => ctx.measureText(c).width);
  const total =
    widths.reduce((a, b) => a + b, 0) + spacingPx * Math.max(0, chars.length - 1);

  let x = -total / 2;
  for (let i = 0; i < chars.length; i++) {
    const w = widths[i];
    // `uniform`  → every glyph tilts the same way (slanted block).
    // `alternate` → glyphs alternate sign by index for a dancing look.
    const tilt = tiltDeg
      ? tiltMode === "alternate"
        ? i % 2 === 0
          ? tiltDeg
          : -tiltDeg
        : tiltDeg
      : 0;
    ctx.save();
    ctx.translate(x + w / 2, y);
    if (tilt) ctx.rotate((tilt * Math.PI) / 180);
    ctx.fillText(chars[i], 0, 0);
    ctx.restore();
    x += w + spacingPx;
  }
}

/** Split a user-supplied text string on newlines (\r\n, \r, or \n). */
function splitLines(text: string): string[] {
  const lines = text.split(/\r\n|\r|\n/);
  // Ensure at least one (possibly empty) line so an all-blank text
  // layer still contributes a sensible bbox height to the editor.
  return lines.length > 0 ? lines : [""];
}

function cssFont(style: TextStyle, size: number): string {
  const parts: string[] = [];
  if (style.italic) parts.push("italic");
  if (style.bold) parts.push("bold");
  parts.push(`${Math.max(1, size)}px`);
  parts.push(style.fontFamily);
  return parts.join(" ");
}

/**
 * Measure a text style's bounding box in texture pixels (at scale = 1).
 * Used to keep the layer's `naturalWidth`/`naturalHeight` in sync with
 * the actual rasterised glyphs so the 2D-editor bbox lines up.
 */
export function measureText(style: TextStyle): {
  width: number;
  height: number;
} {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return { width: Math.max(1, style.fontSize), height: style.fontSize };
  }
  ctx.font = cssFont(style, style.fontSize);

  const lines = splitLines(style.text);
  const spacingPx = letterSpacingOf(style);
  const tiltDeg = letterTiltOf(style);
  const tiltRad = (Math.abs(tiltDeg) * Math.PI) / 180;

  // Width per line = Σ glyph widths + (n − 1) · letter spacing.
  // Take the widest line as the overall block width.
  let maxLineWidth = 0;
  for (const line of lines) {
    const chars = Array.from(line || " ");
    let w = 0;
    for (const c of chars) w += ctx.measureText(c).width;
    w += Math.max(0, chars.length - 1) * spacingPx;
    if (w > maxLineWidth) maxLineWidth = w;
  }

  // Base height from typographic metrics of the first line.
  const firstMetrics = ctx.measureText(lines[0] || " ");
  const asc =
    firstMetrics.actualBoundingBoxAscent &&
    Number.isFinite(firstMetrics.actualBoundingBoxAscent)
      ? firstMetrics.actualBoundingBoxAscent
      : style.fontSize * 0.8;
  const desc =
    firstMetrics.actualBoundingBoxDescent &&
    Number.isFinite(firstMetrics.actualBoundingBoxDescent)
      ? firstMetrics.actualBoundingBoxDescent
      : style.fontSize * 0.2;
  const firstLineHeightBase = asc + desc;
  const extraLineHeight = style.fontSize * lineHeightOf(style);
  let totalHeight =
    firstLineHeightBase + Math.max(0, lines.length - 1) * extraLineHeight;

  // Tilt enlarges each glyph's apparent bbox. Approximate how much
  // the overall block grows by |glyphW·sin(θ)| + |glyphH·cos(θ)|
  // being the new glyph height, and similarly for width. Avoids the
  // selection rectangle clipping tilted ascenders/descenders.
  if (tiltRad > 0) {
    const avgCharW = maxLineWidth > 0 && lines[0]
      ? maxLineWidth / Math.max(1, Array.from(lines[0]).length)
      : style.fontSize * 0.5;
    const glyphH = firstLineHeightBase;
    const tiltHeightBump = avgCharW * Math.sin(tiltRad);
    const tiltWidthBump = glyphH * Math.sin(tiltRad);
    totalHeight += tiltHeightBump;
    maxLineWidth += tiltWidthBump;
  }

  return {
    width: Math.max(1, Math.ceil(maxLineWidth)),
    height: Math.max(1, Math.ceil(totalHeight)),
  };
}

function clamp(v: number, lo: number, hi: number): number {
  if (!Number.isFinite(v)) return lo;
  return Math.max(lo, Math.min(hi, v));
}

function drawDebugOverlay(
  ctx: CanvasRenderingContext2D,
  config: WearableConfig,
): void {
  const boxes = [
    { box: config.frontBox, color: "rgba(255, 70, 70, 0.95)", label: "FRONT" },
    { box: config.backBox, color: "rgba(70, 150, 255, 0.95)", label: "BACK" },
  ];
  for (const { box, color, label } of boxes) {
    const axes = getShirtAxes(box);
    ctx.save();
    ctx.lineWidth = 6;
    ctx.strokeStyle = color;
    ctx.setLineDash([16, 10]);
    ctx.strokeRect(box.x, box.y, box.width, box.height);
    ctx.setLineDash([]);

    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(axes.cx, axes.cy, 14, 0, Math.PI * 2);
    ctx.fill();

    const axisLen = Math.min(box.width, box.height) * 0.4;
    ctx.lineWidth = 8;
    ctx.strokeStyle = color;
    // Shirt-right = solid arrow.
    drawArrow(
      ctx,
      axes.cx,
      axes.cy,
      axes.cx + axes.right[0] * axisLen,
      axes.cy + axes.right[1] * axisLen,
    );
    // Shirt-up = dashed arrow.
    ctx.setLineDash([10, 8]);
    drawArrow(
      ctx,
      axes.cx,
      axes.cy,
      axes.cx + axes.up[0] * axisLen,
      axes.cy + axes.up[1] * axisLen,
    );
    ctx.setLineDash([]);

    ctx.fillStyle = "rgba(0,0,0,0.85)";
    ctx.fillRect(box.x + 4, box.y + 4, 160, 40);
    ctx.fillStyle = color;
    ctx.font = "bold 28px sans-serif";
    ctx.fillText(label, box.x + 16, box.y + 34);
    ctx.restore();
  }
}

function drawArrow(
  ctx: CanvasRenderingContext2D,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x0, y0);
  ctx.lineTo(x1, y1);
  ctx.stroke();
  const ang = Math.atan2(y1 - y0, x1 - x0);
  const head = 18;
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(
    x1 - Math.cos(ang - Math.PI / 6) * head,
    y1 - Math.sin(ang - Math.PI / 6) * head,
  );
  ctx.moveTo(x1, y1);
  ctx.lineTo(
    x1 - Math.cos(ang + Math.PI / 6) * head,
    y1 - Math.sin(ang + Math.PI / 6) * head,
  );
  ctx.stroke();
}

function clamp01(v: number): number {
  if (!Number.isFinite(v)) return 1;
  return Math.max(0, Math.min(1, v));
}

/** Render `source` into `target` at its own resolution (fit). */
export function drawScaledPreview(
  source: HTMLCanvasElement,
  target: HTMLCanvasElement,
): void {
  const ctx = target.getContext("2d");
  if (!ctx) return;
  ctx.clearRect(0, 0, target.width, target.height);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(source, 0, 0, target.width, target.height);
}
